/**
 * Prompt input bar for the plugin builder.
 * Handles text input, send, stop generation, and reset context.
 */

import { useRef, useEffect, KeyboardEvent } from 'react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { Loader2, Send, RotateCcw, Square } from 'lucide-react';

interface PluginPromptInputProps {
  value: string;
  onChange: (value: string) => void;
  onSubmit: () => void;
  onStopGeneration?: () => void;
  onResetContext?: () => Promise<void>;
  isProcessing: boolean;
  isResetting?: boolean;
  hasMessages: boolean;
  placeholder?: string;
  className?: string;
  /**
   * Read-only session (moved to Fias Forge, ADR-023 D6): the textarea and
   * send/reset controls are disabled — the server would answer 409 anyway.
   */
  disabled?: boolean;
}

export function PluginPromptInput({
  value,
  onChange,
  onSubmit,
  onStopGeneration,
  onResetContext,
  isProcessing,
  isResetting,
  hasMessages,
  placeholder,
  className,
  disabled = false,
}: PluginPromptInputProps) {
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    textareaRef.current?.focus();
  }, []);

  const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      if (!disabled) onSubmit();
    }
  };

  return (
    <div className={cn('border-t p-4', className)}>
      <div className="flex gap-2">
        <Textarea
          ref={textareaRef}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={
            disabled
              ? 'This session is read-only — continue in Fias Forge.'
              : (placeholder ?? 'Describe what you want to build...')
          }
          disabled={disabled || isProcessing || isResetting}
          className="min-h-[80px] resize-none"
        />
        <div className="flex flex-col gap-2">
          {isProcessing ? (
            <Button
              variant="destructive"
              size="icon"
              className="flex-1"
              title="Stop generation"
              onClick={onStopGeneration}
              disabled={!onStopGeneration}
            >
              <Square className="h-4 w-4" />
            </Button>
          ) : (
            <Button
              onClick={onSubmit}
              disabled={disabled || !value.trim() || isResetting}
              size="icon"
              className="flex-1"
              aria-label="Send"
            >
              <Send className="h-4 w-4" />
            </Button>
          )}
          {onResetContext && hasMessages && (
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button
                  variant="outline"
                  size="icon"
                  disabled={disabled || isProcessing || isResetting}
                  title="Reset conversation context"
                >
                  {isResetting ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <RotateCcw className="h-4 w-4" />
                  )}
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Reset Conversation Context</AlertDialogTitle>
                  <AlertDialogDescription>
                    This will clear the conversation history but keep your current plugin files
                    intact. You can continue building from the current state with fresh context.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                  <AlertDialogAction onClick={onResetContext}>Reset Context</AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          )}
        </div>
      </div>
      <p className="text-xs text-muted-foreground mt-2">
        Press Enter to send, Shift+Enter for new line
      </p>
    </div>
  );
}
